import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Calendar, Clock, User, Loader, CheckCircle, Stethoscope, Crown, ArrowLeft } from 'lucide-react';
import MembershipOffer from './steps/MembershipOffer';
import CheckoutPage from './steps/CheckoutPage';
import PaymentConfirmation from './steps/PaymentConfirmation';
import MembershipCheckout from './MembershipCheckout';
import VerificationFlow from './steps/VerificationFlow';
import { createUserInFirebase, loginUserInFirebase } from '../../services/FirebaseService';
import { createIncludedConsultation } from '../../services/consultationService';
import { saveAuthTokens, saveUserSession, buildAppRedirectUrl } from '../../utils/checkoutHelpers';

const CheckoutFlow = ({
  userData,
  selectedSpecialty,
  selectedDate,
  selectedTime,
  selectedSlot,
  specialtyConfig,
  selectedDoctor,
  onPaymentComplete,
  onBack
}) => {
  const [currentStep, setCurrentStep] = useState('membership');
  const [checkoutData, setCheckoutData] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userAuthData, setUserAuthData] = useState({
    userId: null,
    authToken: null,
    firebaseUid: null
  });
  const [consultationReason, setConsultationReason] = useState('');
  const [schedulingConsultation, setSchedulingConsultation] = useState(false);

  /**
   * Crea usuario real en Firebase cuando llegamos a checkout
   * Se ejecuta una sola vez al montar el componente
   */
  useEffect(() => {
    const createRealUserFromLead = async () => {
      // Validaciones iniciales
      if (!userData?.leadId) {
        return;
      }

      // Evitar ejecuciones duplicadas
      if (userAuthData.userId || userAuthData.firebaseUid) {
        return;
      }

      try {
        setIsCreatingUser(true);

        // Registro en Firebase
        const firebaseResult = await createUserInFirebase({
          email: userData.email,
          password: userData.password,
          nombre: userData.nombre,
          apellido: userData.apellido,
          telefono: userData.telefono,
          birthDate: userData.birthDate,
          birthSex: userData.birthSex,
          pronoun: userData.pronoun,
          phoneCountry: userData.phoneCountry,
          phoneCode: userData.phoneCode,
          leadId: parseInt(userData.leadId)
        });

        if (!firebaseResult.success) {
          // Si ya existe, intentamos login para recuperar el UID
          if (firebaseResult.errorCode === 'auth/email-already-in-use') {
            const loginResult = await loginUserInFirebase(userData.email, userData.password);
            if (loginResult.success) {
              await associateUserWithOdoo(loginResult.firebaseUid, loginResult.firebaseToken);
              return;
            }
          }
          throw new Error(firebaseResult.error);
        }

        await associateUserWithOdoo(firebaseResult.firebaseUid, firebaseResult.firebaseToken);

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message,
          confirmButtonColor: '#786ED9'
        });
      } finally {
        setIsCreatingUser(false);
      }
    };

    /**
     * Asocia el usuario de Firebase con Odoo
     */
    const associateUserWithOdoo = async (firebaseUid, firebaseToken) => {
      const response = await fetch('https://odoo.core.daisies.mx/marketing-manager/import-data', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          params: {
            access_token: process.env.REACT_APP_ACCESS_TOKEN,
            account_id: 1,
            res_model: "crm.lead",
            res_method: "api_create_dais_user_from_lead",
            res_id: 1,
            res_params: {
              firebase_uid: firebaseUid,
              lead_id: parseInt(userData.leadId)
            }
          }
        })
      });

      const data = await response.json();

      if (data.result?.success) {
        const userId = data.result.dais_user_id;

        setUserAuthData({
          userId: userId,
          authToken: firebaseToken,
          firebaseUid: firebaseUid
        });

        saveAuthTokens(userId, firebaseToken, firebaseUid);

        // Persistir sesión completa con cookie de 15 días
        saveUserSession(
          { userId, authToken: firebaseToken, firebaseUid },
          {
            nombre: userData.nombre,
            apellido: userData.apellido,
            email: userData.email,
            telefono: userData.telefono,
            phoneCountry: userData.phoneCountry,
            phoneCode: userData.phoneCode,
            birthDate: userData.birthDate,
            birthSex: userData.birthSex,
            pronoun: userData.pronoun,
            leadId: userData.leadId
          }
        );

      } else {
        throw new Error(data.result?.error || "Error en Odoo");
      }
    };

    createRealUserFromLead();
  }, [userData?.leadId]);

  const handleContinueToCheckout = (data) => {
    if (data.type === 'membership') {
      setCheckoutData(data);
      setCurrentStep('membershipCheckout');
      return;
    }
    setCheckoutData(data);
    setCurrentStep('checkout');
  };

  const handlePaymentComplete = (result) => {
    setPaymentResult(result);
    setCurrentStep('confirmation');

    // Notificar al componente padre
    if (onPaymentComplete) {
      onPaymentComplete(result);
    }
  };

  const handleCompleteMedicalRecord = () => {
    const redirectUrl = buildAppRedirectUrl(userData, userAuthData);
    window.location.href = redirectUrl;
  };

  const handleStartVerification = () => {
    setCurrentStep('verification');
  };

  const handleVerificationComplete = () => {
    setCurrentStep('confirmation');
  };

  const handleReturnHome = () => {
    window.location.href = '/';
  };

  const handleBackStep = () => {
    switch (currentStep) {
      case 'checkout':
        setCurrentStep('membership');
        break;
      case 'membershipCheckout':
        setCurrentStep('membership');
        break;
      case 'scheduleIncluded':
        // Can't go back to payment, so just stay
        break;
      case 'confirmation':
        setCurrentStep('checkout');
        break;
      default:
        if (onBack) onBack();
    }
  };

  // ─── Schedule included consultation after membership purchase ───
  const handleScheduleIncluded = async () => {
    const authToken = userAuthData.authToken || localStorage.getItem('daisies_auth_token') || '';
    const userId = userAuthData.userId || userData?.userId || localStorage.getItem('daisies_user_id');
    const specConfig = specialtyConfig?.[selectedSpecialty] || {};
    const doctorId = selectedSlot?.professional_ids?.[0]?.id || null;
    const durationId = specConfig.duration;

    if (!authToken || !userId) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se encontró tu sesión. Intenta iniciar sesión de nuevo.', confirmButtonColor: '#786ED9' });
      return;
    }

    setSchedulingConsultation(true);
    try {
      const result = await createIncludedConsultation(authToken, {
        doctor_id: doctorId,
        user_id: parseInt(userId),
        date: selectedDate ? selectedDate.toISOString().split('T')[0] : '',
        duration_id: durationId,
        schedule_id: selectedSlot?.id,
        reason: consultationReason.trim() || 'Primera consulta con membresía',
        reminder: true,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City',
        parent_id: null,
        consultation_reason_id: null,
        specialty_id: specConfig.id || parseInt(selectedSpecialty)
      });

      if (result.success) {
        setPaymentResult(prev => ({ ...prev, ...result, consultationScheduled: true }));
        setCurrentStep('confirmation');
        if (onPaymentComplete) onPaymentComplete({ ...paymentResult, ...result, consultationScheduled: true });
      } else {
        Swal.fire({ icon: 'error', title: 'Error al agendar', text: result.error || 'No se pudo crear la consulta.', confirmButtonColor: '#786ED9' });
      }
    } catch (err) {
      console.error('[CheckoutFlow] handleScheduleIncluded error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error al agendar tu consulta.', confirmButtonColor: '#786ED9' });
    } finally {
      setSchedulingConsultation(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'membership':
        return (
          <MembershipOffer
            selectedSpecialty={selectedSpecialty}
            specialtyConfig={specialtyConfig}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedSlot={selectedSlot}
            selectedDoctor={selectedDoctor}
            userData={{
              ...userData,
              ...userAuthData
            }}
            onContinueToCheckout={handleContinueToCheckout}
            onBack={onBack}
            isCreatingUser={isCreatingUser}
          />
        );

      case 'checkout':
        return (
          <CheckoutPage
            checkoutData={checkoutData}
            userData={{
              ...userData,
              ...userAuthData
            }}
            selectedSpecialty={selectedSpecialty}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedSlot={selectedSlot}
            selectedDoctor={selectedDoctor}
            specialtyConfig={specialtyConfig}
            onPaymentComplete={handlePaymentComplete}
            onBack={handleBackStep}
          />
        );

      case 'membershipCheckout':
        return (
          <MembershipCheckout
            membershipPlan={{
              name: checkoutData?.plan?.name || 'Membresía Daisies',
              // Precio total real del periodo (viene del backend vía MembershipOffer).
              // MembershipCheckout re-lee el catálogo visible (is_shown) por su
              // cuenta; esto solo pre-selecciona y muestra mientras carga.
              price: String(
                checkoutData?.plan?.totalPrice ??
                checkoutData?.plan?.price ??
                '399'
              ),
              // id == odooId == membershipId: pre-selecciona el plan en el checkout.
              membershipId: checkoutData?.plan?.membershipId || checkoutData?.plan?.odooId || 21,
              features: (checkoutData?.plan?.features || []).map(f => ({ text: f })),
              highlighted: true
            }}
            userId={userData?.userId || localStorage.getItem('daisies_user_id')}
            onSuccess={(result) => {
              setPaymentResult(result);
              // After membership payment, route to schedule the included consultation
              setCurrentStep('scheduleIncluded');
            }}
            onBack={handleBackStep}
          />
        );

      case 'confirmation':
        return (
          <PaymentConfirmation
            paymentResult={paymentResult}
            userData={userData}
            selectedSpecialty={selectedSpecialty}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            specialtyConfig={specialtyConfig}
            selectedSlot={selectedSlot}
            checkoutData={checkoutData}
            onCompleteMedicalRecord={handleCompleteMedicalRecord}
            onStartVerification={handleStartVerification}
            onReturnHome={handleReturnHome}
          />
        );

      case 'scheduleIncluded': {
        const specConfig = specialtyConfig?.[selectedSpecialty] || {};
        const specialtyName = specConfig.name || 'Consulta';
        const doctorName = selectedSlot?.professional_name || selectedSlot?.professional_ids?.[0]?.name || 'Doctora';
        const formattedDate = selectedDate
          ? selectedDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })
          : '';

        return (
          <div className="max-w-lg mx-auto px-4 py-4 md:py-8 min-h-screen">
            {/* Header */}
            <div className="text-center mb-6 animate-fadeInUp">
              <div className="mx-auto w-20 h-20 rounded-full bg-daisies flex items-center justify-center mb-4">
                <Crown size={36} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-2 tracking-tight">
                ¡Membresía activada!
              </h2>
              <p className="text-text-secondary text-sm">
                Estás a punto de estrenar tu membresía. Confirma los detalles de tu primera consulta.
              </p>
            </div>

            {/* Summary card */}
            <div className="bg-white rounded-card p-5 md:p-6 mb-4 animate-fadeInUp-1">
              <h3 className="ds-caption text-text-secondary uppercase mb-4">Tu primera consulta</h3>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-tint text-brand shrink-0">
                    <Stethoscope size={20} aria-hidden="true" />
                  </span>
                  <div>
                    <p className="ds-caption text-text-secondary">Especialidad</p>
                    <p className="font-semibold text-text-primary">{specialtyName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-daisies-50 flex items-center justify-center">
                    <User size={16} className="text-daisies" />
                  </div>
                  <div>
                    <p className="ds-caption text-text-secondary">Profesional</p>
                    <p className="font-semibold text-text-primary">{doctorName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-daisies-50 flex items-center justify-center">
                    <Calendar size={16} className="text-daisies" />
                  </div>
                  <div>
                    <p className="ds-caption text-text-secondary">Fecha</p>
                    <p className="font-semibold text-text-primary capitalize">{formattedDate}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-daisies-50 flex items-center justify-center">
                    <Clock size={16} className="text-daisies" />
                  </div>
                  <div>
                    <p className="ds-caption text-text-secondary">Hora</p>
                    <p className="font-semibold text-text-primary">{selectedTime || selectedSlot?.init}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5 p-3 rounded-input bg-mint-pill flex items-center gap-2">
                <CheckCircle size={18} className="text-mint-deep flex-shrink-0" />
                <p className="text-sm text-mint-deep font-medium">Incluida con tu membresía, sin costo adicional</p>
              </div>
            </div>

            {/* Reason */}
            <div className="bg-white rounded-card p-5 md:p-6 mb-6 animate-fadeInUp-2">
              <label className="block text-sm font-semibold text-text-primary mb-2">
                Motivo de consulta
              </label>
              <textarea
                value={consultationReason}
                onChange={e => setConsultationReason(e.target.value)}
                placeholder="Describe brevemente el motivo de tu consulta (opcional)..."
                rows={3}
                className="w-full p-4 rounded-input bg-warm-canvas focus:ring-2 focus:ring-brand focus:outline-none text-text-primary resize-none transition-all"
              />
            </div>

            {/* Confirm button */}
            <button
              onClick={handleScheduleIncluded}
              disabled={schedulingConsultation}
              className={`press-scale w-full py-4 rounded-full text-white font-semibold text-lg transition-all ${!schedulingConsultation
                  ? 'bg-daisies hover:bg-daisies-hover'
                  : 'bg-daisies/40 cursor-not-allowed'
                }`}
            >
              {schedulingConsultation ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader size={20} className="animate-spin" /> Agendando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Stethoscope size={20} /> Confirmar y agendar consulta
                </span>
              )}
            </button>
          </div>
        );
      }

      case 'verification':
        return (
          <VerificationFlow
            userData={{
              ...userData,
              ...userAuthData
            }}
            onComplete={handleVerificationComplete}
            onBack={() => setCurrentStep('confirmation')}
          />
        );

      default:
        return (
          <MembershipOffer
            selectedSpecialty={selectedSpecialty}
            specialtyConfig={specialtyConfig}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedSlot={selectedSlot}
            selectedDoctor={selectedDoctor}
            userData={{
              ...userData,
              ...userAuthData
            }}
            onContinueToCheckout={handleContinueToCheckout}
            onBack={onBack}
            isCreatingUser={isCreatingUser}
          />
        );
    }
  };

  return <div>{renderStep()}</div>;
};

export default CheckoutFlow;
